import React, { useState, useMemo, useEffect } from 'react'

import {
  CompanyId,
  DeleteAnnouncements$Request,
  GetAnnouncementFilters$Request,
  GetAnnouncementFilters$Response,
  GetAnnouncements$Request,
  Lang,
  NotificationDto,
  NotificationId,
  NotificationRow,
  UserCompanyDto,
} from '../../services/api.types'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { Card } from '../../components/Card/Card'
import { Box, Grid, IconButton } from '@material-ui/core'
import { t } from 'ttag'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'

import { type MRT_ColumnDef, MRT_RowSelectionState } from 'material-react-table'
import { getShortDateWithTimeString } from '../../common/helpers/date'
import YumButton from '../../components/YumButton/YumButton'
import { darkBlue, red } from '../../styles/material-ui-theme-v2'
import { currentLangString, isRtl } from '../../i18nInit'
import AnnouncementStatus from './components/AnnouncementStatus/AnnouncementStatus'
import YumTable from '../../components/YumTable/YumTable'

import { selectUserCompany } from '../Dashboard/Dashboard'
import { ROUTES } from '../../common/Routes/routePaths'
import { ReactComponent as EditIcon } from '../../assets/v3/icons/dashboard/edit.svg'
import YumTooltip from '../../components/YumTooltip/YumTooltip'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { getCountryName } from '../../components/YumCountrySelect/YumCountrySelect'

export const getTranslatedLanguageName = (code: string, locale = 'en') => {
  try {
    const fixedCode = code === 'ZH_HANS' ? 'zh-Hans' : code === 'PT_BR' ? 'pt-BR' : code
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' })
    return displayNames.of(fixedCode)
  } catch (e) {
    console.error(`Language code "${code}" not found.`, e)
    return code
  }
}

export interface IAnnouncementsProps {
  selectedCompanyId: CompanyId | null
  managedCompanies: UserCompanyDto[]
  announcements: NotificationDto[]
  isLoading: boolean
  lastUpdate: number | null
  filters: GetAnnouncementFilters$Response
}

export interface IAnnouncementsDispatchProps {
  setSelectedCompanyId(payload: CompanyId): void
  getAnnouncements(payload: GetAnnouncements$Request): void
  deleteAnnouncements(payload: DeleteAnnouncements$Request): void
  getAnnouncementFilters(payload: GetAnnouncementFilters$Request): void
}

export default function Announcements(props: IAnnouncementsProps & IAnnouncementsDispatchProps) {
  const {
    selectedCompanyId,
    managedCompanies,
    announcements,
    isLoading,
    lastUpdate,
    filters,

    setSelectedCompanyId,
    getAnnouncements,
    deleteAnnouncements,
    getAnnouncementFilters,
  } = props

  const [userCompany, setUserCompany] = useState(selectUserCompany(managedCompanies, selectedCompanyId))

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

  const loadAnnouncements = () => {
    const { companyId } = userCompany
    getAnnouncements({ companyId })
    getAnnouncementFilters({
      companyId,
      scheduledTime: null,
      country: null,
      divisionId: null,
      goalId: null,
      lang: null,
      scheduledTimeZone: null,
      editMode: false,
      notificationId: null,
    })
  }

  /*useEffect(() => {
    // Set up an interval to update the time every minute
    const interval = setInterval(() => {
      const { companyId } = userCompany
      getAnnouncements({ companyId })
    }, 60000) // 60000 ms = 1 minute

    // Clean up the interval when the component is unmounted
    return () => clearInterval(interval)
  }, [])*/

  const resetRowSelection = () => {
    setRowSelection({})
  }

  React.useEffect(() => {
    loadAnnouncements()
    resetRowSelection()
  }, [userCompany?.companyId, lastUpdate])

  React.useEffect(() => {
    setUserCompany(managedCompanies.find((company) => company.companyId === userCompany.companyId) as UserCompanyDto)
  }, [managedCompanies])

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: CompanyId }
    setUserCompany(managedCompanies.find((company) => company.companyId === value) as UserCompanyDto)

    setSelectedCompanyId(value)
  }

  const handleDelete = (table: any) => async () => {
    const notificationIds = table.getSelectedRowModel().flatRows.map(({ original: { id } }: any) => id)

    /*const x = announcements.filter((n) => notificationIds.includes(n.id)).reduce((sum, d) => sum + d.members, 0)
    const { value } = await CapacitorDialog.confirm({
      title: t`Confirm`,
      message: t`Are you sure? ${x} users will lose their team.`,
    })
    if (!value) return*/

    deleteAnnouncements({ notificationIds })
  }

  const openEdit = (id: NotificationId) => () => {
    store.dispatch(push(`/admin/announcements/${id}/edit`))
  }

  const getFilters = (n: NotificationDto) => {
    const f: React.ReactNode[] = []
    if (n.goalId) {
      f.push(
        <>
          {t`Challenge`}: {/* @ts-ignore */}
          {filters.challenges.find((c: any) => c.id === n.goalId)?.title || <em>{t`Deleted challenge`}</em>}
        </>,
      )
    }
    if (n.divisionId) {
      /* @ts-ignore */
      f.push(
        <>
          {t`Team`}: {/* @ts-ignore */}
          {filters.teams.find((c: any) => c.id === n.divisionId)?.title || <em>{t`Deleted team`}</em>}
        </>,
      )
    }
    if (n.country) {
      f.push(`${t`Country`}: ${getCountryName(n.country)}`)
    }
    if (n.lang) {
      f.push(`${t`Language`}: ${getTranslatedLanguageName(n.lang, currentLangString)}`)
    }

    return (
      <>
        {f.length > 0
          ? f.map((filter, index) => (
              <span key={index}>
                {filter}
                {index < f.length - 1 && ', '}
              </span>
            ))
          : t`No filters`}
      </>
    )
    //return <>{f.length > 0 ? f.join(', ') : t`No filters`}</>
  }

  const columns = useMemo<MRT_ColumnDef<NotificationDto>[]>(
    () => [
      {
        accessorKey: 'title',
        header: t`Title`,
        sortingFn: 'alphanumeric',
      },
      /*{
        accessorKey: 'lang',
        header: t`Language`,
        // sortingFn: 'alphanumeric',
      },*/
      {
        accessorKey: 'scheduledTime',
        header: t`Scheduled time`,
        // Convert to Date for sorting and filtering
        accessorFn: (row) => new Date(row.scheduledTime),
        sortingFn: 'datetime',
        Cell: ({
          row: {
            original: { scheduledTime },
          },
        }: any) => getShortDateWithTimeString(scheduledTime),
      },
      {
        accessorKey: 'sent',
        header: t`Status`,
        Cell: ({ row: { original: notification } }: any) => <AnnouncementStatus notification={notification} />,
        accessorFn: (n) => n.sent + ' ' + n.readBy,
      },
      {
        //accessorKey: 'sent',
        header: t`Filters`,
        Cell: ({ row: { original: notification } }: any) => getFilters(notification),
        //accessorFn: (n) => n.sent + ' ' + n.readBy,
      },
      {
        accessorKey: 'createdAt',
        header: t`Created`,
        // Convert to Date for sorting and filtering
        accessorFn: (row) => new Date(row.createdAt),
        sortingFn: 'datetime',
        Cell: ({
          row: {
            original: { createdAt },
          },
        }: any) => getShortDateWithTimeString(createdAt),
      },
      {
        accessorKey: 'updatedAt',
        header: t`Updated`,
        // Convert to Date for sorting and filtering
        accessorFn: (row) => new Date(row.updatedAt),
        sortingFn: 'datetime',
        Cell: ({
          row: {
            original: { updatedAt },
          },
        }: any) => getShortDateWithTimeString(updatedAt),
      },
      {
        accessorKey: 'edit',
        enableSorting: false,
        enableColumnFilter: false,
        header: t`Edit`,
        size: 50,
        Cell: ({
          row: {
            original: { id },
          },
        }: any) => (
          <YumTooltip title={t`Edit`} arrow placement="top">
            <IconButton aria-label="edit" onClick={openEdit(id)} style={{ borderRadius: '10px' }}>
              <EditIcon
                style={{
                  width: 20,
                  height: 20,
                }}
              />
            </IconButton>
          </YumTooltip>
        ),
      },
    ],
    [],
  )

  // return <>x: {JSON.stringify(announcements, null, 2)}</>

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>
          {t`Announcements`}
          {!isLoading && <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}> ({announcements.length})</span>}
        </h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect
            variant="outlined"
            value={userCompany.companyId}
            onChange={handleCompanyChange}
            inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
            className="show-on-desktop"
          >
            {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
              <option key={keyIndex} value={userCompany.companyId}>
                {userCompany.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>
      {managedCompanies.length > 1 && (
        <YumSmallSelect
          variant="outlined"
          value={userCompany.companyId}
          onChange={handleCompanyChange}
          inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
          className="hide-on-desktop"
          fullWidth
          //style={{ marginBottom: 16 }}
        >
          {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
            <option key={keyIndex} value={userCompany.companyId}>
              {userCompany.companyName}
            </option>
          ))}
        </YumSmallSelect>
      )}

      <div style={{ marginTop: '10px' }}>
        <YumTable
          enableRowNumbers={false}
          data={announcements}
          columns={columns as any}
          enableRowSelection
          enableExpanding={false}
          getRowId={(row: any) => row.id as string}
          onRowSelectionChange={setRowSelection}
          state={{ rowSelection, isLoading }}
          initialState={{
            sorting: [
              {
                id: 'scheduledTime',
                desc: true,
              },
            ],
            density: 'compact',
            columnPinning: { right: ['edit'] },
          }}
          muiTableBodyCellProps={({ row }) => ({
            /*onClick: (event) => {
              row.toggleExpanded()
            },*/
            sx: (theme) => ({
              paddingTop: '14px !important',
              paddingBottom: '14px !important',
              lineHeight: '28px !important',
              fontSize: '0.875rem !important',
              color: darkBlue,
            }),
          })}
          muiTableBodyRowProps={({ row }) => ({
            sx: (theme) => ({
              //cursor: 'pointer',
              /*...(row.getIsExpanded() && {
                backgroundColor: gray,
                MuiTableCell: {
                  padding: '0px',
                },
              }),*/
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 111, 255, 0.1)',
                '& > td': {
                  backgroundColor: 'transparent',
                },
              },
            }),
          })}
          /*renderDetailPanel={({ row }) => (
            <Box
              sx={{
                display: 'block',
                width: '100%',
                maxWidth: '100%',
              }}
            >
              <TeamMembersTableWrapper companyId={userCompany.companyId} divisionId={row.id} />
            </Box>
          )}*/
          /*muiTopToolbarProps={{
            sx: {
              '& > .MuiBox-root': {
                alignItems: 'center',
                padding: '0px',
                '.MuiDialog-scrollPaper &': {
                  padding: '8px',
                },
              },
            },
          }}*/
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ display: 'flex', gridGap: '0.5rem', p: '0px' }}>
              {!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected() ? (
                <>
                  <YumButton
                    size="small"
                    color="secondary"
                    to={ROUTES.ADMIN_ANNOUNCEMENTS_CREATE.to}
                  >{t`Create announcement`}</YumButton>
                </>
              ) : (
                <>
                  <YumButton onClick={handleDelete(table)} variant="contained" size="small" color="error">
                    {t`Delete`}
                  </YumButton>
                </>
              )}
            </Box>
          )}
        />
      </div>
    </Card>
  )
}
